// Map Component - Refactored for Better Readability

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { MAP_TYPES } from './map-utils';
import { DepotMarker, EndDepotMarker, SimpleCustomerMarkers, RouteCustomerMarkers } from './MapMarkers';
import { RoutePolylines } from './RoutePolylines';
import { PolygonDrawing } from './PolygonDrawing';
import { PolygonInstructions } from './PolygonInstructions';
import { MultiAreaOverlay } from './MultiAreaOverlay';

import { usePolygonDrawing } from '../hooks/usePolygonDrawing';
import { useMultiAreaSelection } from '../hooks/useMultiAreaSelection';

import { Customer, Route } from '../../../../api/mobiApi/domain/domain.types';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Helper component to get map instance
const MapInstanceGetter: React.FC<{ onMapReady: (map: L.Map) => void }> = ({ onMapReady }) => {
  const map = useMap();
  useEffect(() => {
    onMapReady(map);
  }, [map, onMapReady]);
  return null;
};

interface MapComponentProps {
  customers?: Customer[];
  routes?: Route[];
  startDepot?: { lat: number; lon: number; name?: string };
  endDepot?: { lat: number; lon: number; name?: string }; // Optional - if undefined, same as startDepot
  center?: [number, number];
  zoom?: number;
  mapType?: string;
  isEditMode?: boolean;
  drawingMode?: 'rectangle' | 'polygon' | 'circle' | 'freehand' | null; // Drawing mode (null = no area selection)
  isMultiAreaMode?: boolean; // Enable multi-area cumulative selection
  onCustomersSelect?: (customers: Customer[]) => void;
  onRouteClick?: (vehicleId: string) => void;
  selectedRouteId?: string;
  onCustomerClick?: (customer: Customer) => void; // Customer click handler
  onTimeWindowChange?: (customerId: string, timeWindow: { start: string; end: string } | undefined) => void;
  onPriorityChange?: (customerId: string, priority: number | undefined) => void;
  focusLocation?: { lat: number; lon: number; zoom?: number } | null; // Focus on a specific location
}

const MapComponent: React.FC<MapComponentProps> = ({
  customers = [],
  routes = [],
  startDepot,
  endDepot,
  center = [49.649205, 8.470092], // Default center (Mannheim, Germany)
  zoom = 12,
  mapType = 'osm',
  isEditMode = false,
  drawingMode = 'rectangle', // Default to rectangle for backward compatibility
  isMultiAreaMode = false,
  onCustomersSelect,
  onRouteClick,
  selectedRouteId,
  onCustomerClick,
  onTimeWindowChange,
  onPriorityChange,
  focusLocation,
}) => {
  const [selectedRoute, setSelectedRoute] = useState<string | null>(null);

  // Sync external selectedRouteId with internal state
  useEffect(() => {
    setSelectedRoute(selectedRouteId || null);
  }, [selectedRouteId]);

  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Update map center when center prop changes (depot coordinates update)
  useEffect(() => {
    if (mapInstance && center) {
      mapInstance.setView(center, mapInstance.getZoom());
    }
  }, [center, mapInstance]);

  // Focus on specific location when focusLocation changes
  useEffect(() => {
    if (mapInstance && focusLocation) {
      mapInstance.flyTo(
        [focusLocation.lat, focusLocation.lon],
        focusLocation.zoom || 16,
        { duration: 0.5 }
      );
    }
  }, [focusLocation, mapInstance]);

  // ResizeObserver to handle container size changes
  useEffect(() => {
    if (!mapInstance || !mapContainerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Invalidate map size when container is resized
      setTimeout(() => {
        // Check if map container is still visible before invalidating
        if (mapContainerRef.current && mapContainerRef.current.offsetParent !== null) {
          try {
            mapInstance.invalidateSize();
          } catch (error) {
            // Silently catch errors during collapse/expand transitions
            console.debug('Map resize skipped during transition');
          }
        }
      }, 100);
    });

    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapInstance]);

  // Multi-area selection hook (for cumulative selection mode)
  const multiAreaSelection = useMultiAreaSelection(mapInstance, customers);

  // Polygon drawing hook (use multi-area if enabled, otherwise single selection)
  const polygonDrawing = usePolygonDrawing(
    mapInstance,
    customers,
    isMultiAreaMode ? undefined : onCustomersSelect
  );





  // Auto-start drawing when entering edit mode
  useEffect(() => {
    if (isEditMode && drawingMode) {
      if (isMultiAreaMode && drawingMode === 'polygon') {
        // Multi-area mode (polygon only)
        if (!multiAreaSelection.isDrawing) {
          multiAreaSelection.startMultiSelection();
        }
      } else {
        // Single selection mode (polygon only)
        if (drawingMode === 'polygon' && !polygonDrawing.isDrawing) {
          polygonDrawing.startDrawing();
        }
      }
    } else {
      // Cancel any active drawing
      if (polygonDrawing.isDrawing) {
        polygonDrawing.cancelDrawing();
      }
      if (multiAreaSelection.isDrawing) {
        multiAreaSelection.cancelMultiSelection();
      }
    }
  }, [isEditMode, drawingMode, isMultiAreaMode]);

  // Keyboard shortcuts (ESC to cancel, Backspace to remove last area in multi-mode)
  useEffect(() => {
    const isAnyDrawing = polygonDrawing.isDrawing || multiAreaSelection.isDrawing;
    if (!isAnyDrawing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isMultiAreaMode && multiAreaSelection.isDrawing) {
          multiAreaSelection.cancelMultiSelection();
        } else if (polygonDrawing.isDrawing) {
          polygonDrawing.cancelDrawing();
        }
      } else if (e.key === 'Enter' && isMultiAreaMode) {
        // Finish multi-area selection
        e.preventDefault();
        if (onCustomersSelect && multiAreaSelection.accumulatedCustomers.length > 0) {
          multiAreaSelection.finishMultiSelection(onCustomersSelect);
        }
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && isMultiAreaMode) {
        // Remove last completed area in multi-area mode
        e.preventDefault();
        multiAreaSelection.removeLastArea();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    polygonDrawing.isDrawing,
    polygonDrawing.cancelDrawing,
    multiAreaSelection.isDrawing,
    multiAreaSelection.cancelMultiSelection,
    multiAreaSelection.finishMultiSelection,
    multiAreaSelection.removeLastArea,
    multiAreaSelection.accumulatedCustomers.length,
    isMultiAreaMode,
    onCustomersSelect
  ]);

  const currentMapType = MAP_TYPES[mapType as keyof typeof MAP_TYPES] || MAP_TYPES.osm;
  const hasRoutes = routes.length > 0;

  const handleRouteClick = (vehicleId: string) => {
    setSelectedRoute(prev => prev === vehicleId ? null : vehicleId);
    if (onRouteClick) {
      onRouteClick(vehicleId);
    }
  };

  const selectedRouteData = selectedRoute
    ? routes.find(r => r.vehicle_id === selectedRoute)
    : null;

  // Filter customers that are NOT in any route
  const customersNotInRoutes = React.useMemo(() => {
    if (!hasRoutes || routes.length === 0) return customers;

    const routeCustomerIds = new Set<string>();
    routes.forEach(route => {
      route.customers?.forEach(id => routeCustomerIds.add(id));
    });

    return customers.filter(customer => !routeCustomerIds.has(customer.id));
  }, [customers, routes, hasRoutes]);

  // Disable map dragging in edit mode
  useEffect(() => {
    if (!mapInstance) return;

    if (isEditMode) {
      mapInstance.dragging.disable();
      mapInstance.scrollWheelZoom.disable();
    } else {
      mapInstance.dragging.enable();
      mapInstance.scrollWheelZoom.enable();
    }
  }, [isEditMode, mapInstance]);

  // Mouse event handlers - polygon mode only
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditMode) return;
    // Polygon mode: do nothing on mouse down (handled by click)
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isEditMode || drawingMode !== 'polygon') return;

    // Update polygon preview
    if (isMultiAreaMode) {
      multiAreaSelection.updateMousePosition(e);
    } else {
      polygonDrawing.updateMousePosition(e);
    }
  };

  const handleMouseClick = (e: React.MouseEvent) => {
    if (!isEditMode || drawingMode !== 'polygon') return;

    // Add point to polygon on left click
    if (isMultiAreaMode) {
      multiAreaSelection.addPoint(e);
    } else {
      polygonDrawing.addPoint(e);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!isEditMode || drawingMode !== 'polygon') return;

    // Prevent default context menu
    e.preventDefault();

    // Finish polygon on right click (min 3 points)
    if (isMultiAreaMode) {
      // Multi-area mode: finish current polygon and continue drawing
      if (multiAreaSelection.currentPoints.length >= 3) {
        multiAreaSelection.finishCurrentPolygon();
      }
    } else {
      // Single selection mode: finish and exit
      if (polygonDrawing.polygonPoints.length >= 3) {
        polygonDrawing.finishDrawing();
      }
    }
  };

  const handleMouseUp = () => {
    // Polygon mode doesn't need mouse up handling
    return;
  };

  return (
    <div
      ref={mapContainerRef}
      className="map-container"
      style={{
        height: '100%',
        width: '100%',
        transition: 'all 0.3s ease-in-out',
        cursor: isEditMode ? 'crosshair' : 'default',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleMouseClick}
      onContextMenu={handleContextMenu}
    >
      {/* Multi-area overlay (shows all completed polygon areas + current drawing) */}
      {isMultiAreaMode && multiAreaSelection.isDrawing && (
        <MultiAreaOverlay
          completedAreas={multiAreaSelection.completedAreas}
          currentPoints={multiAreaSelection.currentPoints}
          currentMousePoint={multiAreaSelection.currentMousePoint}
          isDrawing={multiAreaSelection.isDrawing}
        />
      )}

      {/* Single selection overlays */}
      {!isMultiAreaMode && (
        <>
          {/* Polygon drawing overlay */}
          {drawingMode === 'polygon' && polygonDrawing.isDrawing && (
            <>
              <PolygonDrawing
                isDrawing={polygonDrawing.isDrawing}
                polygonPoints={polygonDrawing.polygonPoints}
                currentMousePoint={polygonDrawing.currentMousePoint}
              />
              <PolygonInstructions pointCount={polygonDrawing.polygonPoints.length} />
            </>
          )}
        </>
      )}

      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <TileLayer
          url={currentMapType.url}
          attribution={currentMapType.attribution}
        />

        {/* Map instance getter */}
        <MapInstanceGetter onMapReady={setMapInstance} />

        {/* Start Depot (Green) */}
        {startDepot && <DepotMarker depot={startDepot} />}

        {/* End Depot (Red) - Only show if different from start depot */}
        {endDepot && <EndDepotMarker depot={endDepot} />}

        {/* Customer Markers - Show customers that are NOT in routes */}
        {customersNotInRoutes.length > 0 && (
          <SimpleCustomerMarkers
            customers={customersNotInRoutes}
            onCustomerClick={onCustomerClick}
            onTimeWindowChange={onTimeWindowChange}
            onPriorityChange={onPriorityChange}
          />
        )}

        {/* Route Customer Markers - Show customers that ARE in routes */}
        {hasRoutes && (
          <RouteCustomerMarkers
            routes={routes}
            selectedRoute={selectedRoute}
            onTimeWindowChange={onTimeWindowChange}
            onPriorityChange={onPriorityChange}
          />
        )}

        {/* Route Polylines */}
        {hasRoutes && (
          <RoutePolylines
            routes={routes}
            selectedRoute={selectedRoute}
            onRouteClick={handleRouteClick}
          />
        )}

     
      </MapContainer>
    </div>
  );
};

// Export hooks
export { usePolygonDrawing, useMultiAreaSelection };

export default MapComponent;
